import React, { useEffect, useMemo, useState } from "react";
import "./Printerset.css";
import { apiFetch } from "../components/api"

const FIELD_META = [
  { key: "width",       label: "라벨 가로(mm)",        type: "float",  min: 10 },
  { key: "height",      label: "라벨 세로(mm)",        type: "float",  min: 10 },
  { key: "gap",         label: "라벨 사이 갭(mm)",      type: "float",  min: 0.5 },
  { key: "dpi",         label: "DPI",                  type: "int",   min: 100 },
  { key: "offset_x",    label: "OFFSET X (dots)",      type: "int"   },
  { key: "offset_y",    label: "OFFSET Y (dots)",      type: "int"   },
  { key: "direction",   label: "방향 (0=정방향,1=180°)",type: "int",   min: 0, max: 1 },
  { key: "font_size",   label: "폰트 크기(px)",         type: "int",   min: 1 },
  { key: "qr_size",     label: "QR 크기(px)",          type: "int",   min: 16 },
  { key: "qr_version",  label: "QR 버전(0=자동,1~40)",  type: "int",   min: 0, max: 40 },
  // text_enable은 아래 토글로 따로 처리
];

const DEFAULT_FORM = {
  width: 10, height: 10, gap: 0.5,
  offset_x: 0, offset_y: 0,
  direction: 1, dpi: 190,
  qr_size: 150, qr_version: 0,
  text_enable: 1, font_size: 22,
  qr_enable: 1,
};

export default function ProfileEditor() {
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [printing, setPrinting] = useState(false);

  // 최초 로드: 프로필 목록
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const j = await apiFetch("/api/print/profile/list/", { method: "GET" });
        const list = Array.isArray(j?.profiles) ? j.profiles : [];
        setProfiles(list);

        const urlP = new URLSearchParams(window.location.search).get("profile");
        const initial = urlP && list.includes(urlP) ? urlP : (list[0] || "");
        setSelected(initial);

        if (initial) {
          await loadProfile(initial);
        }
      } catch (e) {
        setErr(`프로필 리스트 로드 실패: ${e.message || "알 수 없는 오류"}`);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async (name) => {
    setErr("");
    setMsg("");
    try {
      setLoading(true);

      // retrieve : tspl.get_profile
      const j = await apiFetch(`/api/print/profile/${encodeURIComponent(name)}/`, { method: "GET" });
      const data = j?.data || {};

      const next = { ...DEFAULT_FORM };
      for (const k of Object.keys(next)) {
        if (k in data) {
          const v = data[k];
          next[k] =
            (k === "width" || k === "height" || k === "gap")
              ? Number(v)
              : Number.isNaN(Number(v)) ? v : Number(v);
        }
      }
      next.text_enable = Number(data.text_enable ?? next.text_enable);

      setForm(next);
      setMsg(`'${name}' 프로필 불러옴`);
    } catch (e) {
      setErr(`프로필 데이터 로드 실패 : ${e.message || "알 수 없는 오류"}`);
    } finally {
      setLoading(false);
    }
  };

  const onChangeField = (key, raw) => {
    setForm((prev) => {
      const next = { ...prev };
      const meta = FIELD_META.find((m) => m.key === key);
      if (!meta) return next;
      let val = raw;
      if (meta.type === "int")   val = raw === "" ? "" : parseInt(raw, 10);
      if (meta.type === "float") val = raw === "" ? "" : parseFloat(raw);
      next[key] = Number.isNaN(val) ? "" : val;
      return next;
    });
  };

  const onToggleText = () =>
    setForm((prev) => ({ ...prev, text_enable: prev.text_enable ? 0 : 1 }));

  const onToggleQr = () =>
    setForm((prev) => ({ ...prev, qr_enable: prev.qr_enable ? 0 : 1 }));

  const validate = () => {
    for (const m of FIELD_META) {
      const v = form[m.key];
      if (v === "" || v === null || v === undefined || Number.isNaN(Number(v))) {
        return `${m.label} 값을 확인하세요.`;
      }
      if (typeof m.min === "number" && Number(v) < m.min) {
        return `${m.label}은(는) 최소 ${m.min} 입니다.`;
      }
      if (typeof m.max === "number" && Number(v) > m.max) {
        return `${m.label}은(는) 최대 ${m.max} 입니다.`;
      }
    }
    return "";
  };

  const saveProfile = async (overrideFields = null) => {
    setErr("");
    setMsg("");
    if (!selected) return setErr("프로필 이름을 먼저 선택/입력하세요.");
    const why = validate();
    if (why) return setErr(why);

    const fields = overrideFields
    ? overrideFields
    : (() => {
        const next = {};
        for (const k of Object.keys(form)) next[k] = form[k];
        return next;
      })();

    try {
      setBusy(true);

      await apiFetch("/api/print/profile/upsert/", {
        method: "POST",
        body: { name: selected, fields },
      });

      // setMsg(`'${selected}' 저장 완료`);

      if (!profiles.includes(selected)) {
        setProfiles((p) => [...p, selected].sort());
      }

      const nameLower = String(selected).trim().toLowerCase();
      if (nameLower.endsWith("_bulk")) {
        setMsg(`'${selected}' 저장 완료 (텍스트 전용 프로필은 기본 프로필로 지정하지 않습니다.)`);
      } else {
        try {
          await apiFetch("/api/print/profile/select/", {
            method: "POST",
            body: { name: selected },
          });
          setMsg(`'${selected}' 저장 및 기본 프로필로 지정 완료`);
        } catch (e) {
          setErr(`기본 프로필 지정 실패 : ${e.message || "알 수 없는 오류"}`);
        }
      }
    } catch (e) {
      setErr(`프로필 저장 실패 : ${e.message || "알 수 없는 오류"}`);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const deleteProfile = async () => {
    setErr("");
    setMsg("");
    if (!selected) return setErr("삭제할 프로필이 없습니다.");
    if (!window.confirm(`'${selected}' 프로필을 삭제할까요?`)) return;

    try {
      setBusy(true);

      await apiFetch(`/api/print/profile/${encodeURIComponent(selected)}/`, {
        method: "DELETE",
      });

      setMsg(`'${selected}' 삭제 완료`);

      const rest = profiles.filter((n) => n !== selected);
      setProfiles(rest);

      const nextName = rest[0] || "";
      setSelected(nextName);
      setForm(DEFAULT_FORM);
      if (nextName) await loadProfile(nextName);
    } catch (e) {
      setErr(`프로필 삭제 실패 : ${e.message || "알 수 없는 오류"}`);
    } finally {
      setBusy(false);
    }
  };

  const createNewProfile = () => {
    setErr("");
    setMsg("");

    const name = prompt("새 프로필 이름을 입력하세요 (예: 40x30)");
    if (!name) return;

    const trimmed = name.trim();
    if (!trimmed) return;    

    if (profiles.includes(trimmed)) {
      setErr(`'${trimmed}' 프로필이 이미 존재합니다.`);
      return;
    }

    setSelected(name.trim());
    setForm({ ...DEFAULT_FORM }); 
    setMsg(`'${name}' 프로필 생성 준비 — 값 입력 후 저장을 누르세요.`);
  };

  const runTestPrint = async (mode) => {
    setErr("");
    setMsg("");
    if (!selected) {
      setErr("프로필 이름이 없습니다.");
      return;
    }

    try {
      setPrinting(true);

      const fields = {};
      for (const k of Object.keys(form)) fields[k] = form[k];

      if (mode === "qr") {
        fields.qr_enable = "1";
      }

      await saveProfile(fields);

      if (mode === "calibration") {
        await apiFetch("/api/print/label/calibration/", {
          method: "POST",
          body: {
            profile: selected,
          },
        });

        setMsg(`✅ '${selected}' 프로필 저장 및 보정 인쇄 완료.`);
        return;
      }
      else if (mode === "qr") {
        await apiFetch("/api/print/label/text/", {
          method: "POST",
          body: {
            profile: selected,
            qty: 1,
            data: [`${selected}-${Date.now()}`],
          },
        });

        setMsg(`✅ '${selected}' 프로필 저장 및 테스트 라벨 인쇄 완료.`);
        return;
      }
    } catch (e) {
      if (mode === "calibration") setErr(`보정 인쇄 실패 : ${e.message || "알 수 없는 오류"}`);
      else if (mode === "qr") setErr(`테스트 인쇄 실패 : ${e.message || "알 수 없는 오류"}`);
    } finally {
      setPrinting(false);
    }
  };

  const locked = loading || busy || printing;
  const canEdit = !!selected && !locked;

  return (
    <div className="pe-container">
      <header className="pe-header">
        <h1>라벨 프로필 편집</h1>
        <div className="pe-actions">
          <button className="pe-btn" onClick={createNewProfile} disabled={locked}>
            새 프로필
          </button>
          {/* <button
            className="pe-btn"
            disabled={!canEdit || busy}
            onClick={setAsDefault}
          >
            기본 프로필로 지정
          </button> */}
          
          <button className="pe-btn" disabled={!canEdit || printing} onClick={() => runTestPrint("qr")}>
            {printing ? "인쇄 중..." : "테스트 인쇄"}
          </button>
          <button className="pe-btn" disabled={!canEdit || printing} onClick={() => runTestPrint("calibration")}>
            {printing ? "인쇄 중..." : "보정 인쇄"}
          </button>
        </div>
      </header>

      <section className="pe-panel">
        <div className="pe-row">
          <label className="pe-label">프로필 선택</label>
          <div className="pe-input-wrap">
            <select
              className="pe-select"
              value={selected}
              disabled={locked}
              onChange={async (e) => {
                const name = e.target.value;
                setSelected(name);
                if (name) await loadProfile(name);
              }}
            >
              <option value="">프로필 선택...</option>
              {profiles.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <button className="pe-btn primary" disabled={!canEdit} onClick={() => saveProfile()}>
              {busy ? "저장 중..." : "저장"}
            </button>
            <button className="pe-btn danger" disabled={!canEdit} onClick={deleteProfile}>삭제</button>
          </div>
        </div>

        <div className="pe-grid">
          {FIELD_META.map((m) => (
            <div className="pe-field" key={m.key}>
              <label className="pe-label" htmlFor={`f-${m.key}`}>{m.label}</label>
              <input
                id={`f-${m.key}`}
                className="pe-input"
                type="number"
                step={m.type === "float" ? "0.1" : "1"}
                min={m.min ?? undefined}
                max={m.max ?? undefined}
                value={form[m.key]}
                onChange={(e) => onChangeField(m.key, e.target.value)}
                disabled={locked}
              />
            </div>
          ))}

          <div className="pe-field">
            <label className="pe-label">QR 표시</label>
            <div className="pe-toggle">
              <input
                id="f-qr-enable"
                type="checkbox"
                checked={!!form.qr_enable}
                onChange={onToggleQr}
                disabled={locked}
              />
              <label htmlFor="f-qr-enable">qr_enable</label>
            </div>
          </div>

          <div className="pe-field">
            <label className="pe-label">하단 텍스트 표시</label>
            <div className="pe-toggle">
              <input
                id="f-text-enable"
                type="checkbox"
                checked={!!form.text_enable}
                onChange={onToggleText}
                disabled={locked}
              />
              <label htmlFor="f-text-enable">text_enable</label>
            </div>
          </div>
        </div>

        <div className="pe-status">
          {loading && <span className="pe-info">불러오는 중...</span>}
          {msg && <span className="pe-success">{msg}</span>}
          {err && <span className="pe-error">오류: {err}</span>}
        </div>
      </section>

      {/* <footer className="pe-footer">
        <small>API: api/print/profile/&lt;name&gt; · upsert · select · &lt;name&gt;/delete</small>
      </footer> */}
    </div>
  );
}
